<template>
    <div class="rich-editor-container">
      <!-- 工具栏 -->
      <div class="editor-toolbar">
        <!-- 字体大小选择 -->
        <select class="toolbar-select" v-model="selectedFontSize" @change="setFontSize">
          <option value="12px">12px</option>
          <option value="14px">14px</option>
          <option value="16px">16px</option>
          <option value="18px">18px</option>
          <option value="20px">20px</option>
          <option value="24px">24px</option>
          <option value="28px">28px</option>
          <option value="32px">32px</option>
        </select>
        <div class="toolbar-divider"></div>
        <button 
          type="button"
          class="toolbar-btn" 
          @click="formatText('bold')" 
          :class="{ active: currentFormat.bold }"
          title="加粗"
        >
          <strong>B</strong>
        </button>
        <button 
          type="button"
          class="toolbar-btn" 
          @click="formatText('italic')" 
          :class="{ active: currentFormat.italic }"
          title="斜体"
        >
          <em>I</em>
        </button>
        <button 
          type="button"
          class="toolbar-btn" 
          @click="formatText('underline')" 
          :class="{ active: currentFormat.underline }"
          title="下划线"
        >
          <u>U</u>
        </button>
        <button 
          type="button"
          class="toolbar-btn" 
          @click="formatText('strikeThrough')" 
          :class="{ active: currentFormat.strikeThrough }"
          title="删除线"
        >
          <s>S</s>
        </button>
        <div class="toolbar-divider"></div>
        <button 
          type="button"
          class="toolbar-btn" 
          @click="formatText('justifyLeft')" 
          :class="{ active: currentFormat.justifyLeft }"
          title="左对齐"
        >
          <span>左</span>
        </button>
        <button 
          type="button"
          class="toolbar-btn" 
          @click="formatText('justifyCenter')" 
          :class="{ active: currentFormat.justifyCenter }"
          title="居中"
        >
          <span>中</span>
        </button>
        <button 
          type="button"
          class="toolbar-btn" 
          @click="formatText('justifyRight')" 
          :class="{ active: currentFormat.justifyRight }"
          title="右对齐"
        >
          <span>右</span>
        </button>
        <div class="toolbar-divider"></div>
        <!-- <button 
          class="toolbar-btn" 
          @click="formatText('insertUnorderedList')" 
          title="无序列表"
        >
          <span>●●●</span>
        </button> -->
        <!-- <button 
          class="toolbar-btn" 
          @click="formatText('insertOrderedList')" 
          title="有序列表"
        >
          <span>1.2.3</span>
        </button> -->
        <div class="toolbar-divider"></div>
        <button 
          type="button"
          class="toolbar-btn" 
          @click="formatText('outdent')" 
          title="减少缩进"
        >
          <span>←</span>
        </button>
        <button 
          type="button"
          class="toolbar-btn" 
          @click="formatText('indent')" 
          title="增加缩进"
        >
          <span>→</span>
        </button>
      </div>
      <!-- 编辑区域 -->
      <div 
        ref="editorContent"
        class="editor-content"
        contenteditable="true"
        @input="handleInput"
        @keydown="handleKeydown"
        @mouseup="updateFormat"
        @keyup="updateFormat"
      ></div>
      <!-- 字数统计 -->
      <div v-if="maxlength > 0" class="editor-footer">
        <span :class="{ 'text-warning': currentLength >= maxlength }">
          {{ currentLength }} / {{ maxlength }}
        </span>
      </div>
    </div>
  </template>
  
  <script setup>
  import { ref, watch, onMounted } from 'vue';
  
  const props = defineProps({
    modelValue: {
      type: String,
      default: ''
    },
    maxlength: {
      type: Number,
      default: 0 // 0 表示不限制
    }
  });
  
  const emit = defineEmits(['update:modelValue']);
  
  const editorContent = ref(null);
  const currentLength = ref(0);
  const selectedFontSize = ref('14px');
  const currentFormat = ref({
    bold: false,
    italic: false,
    underline: false,
    strikeThrough: false,
    justifyLeft: false,
    justifyCenter: false,
    justifyRight: false
  });
  
  const formatText = (command) => {
    document.execCommand(command, false, null);
    updateFormat();
  };
  
  // 设置字体大小
  const setFontSize = () => {
    document.execCommand('fontSize', false, getFontSizeValue(selectedFontSize.value));
  };
  
  // 将字体大小转换为execCommand所需的数值
  const getFontSizeValue = (size) => {
    const sizeMap = {
      '12px': 1,
      '14px': 2,
      '16px': 3,
      '18px': 4,
      '20px': 5,
      '24px': 6,
      '28px': 7,
      '32px': 7
    };
    return sizeMap[size] || 3;
  };
  
  // 按键事件处理，防止超过字数限制
  const handleKeydown = (e) => {
    if (!editorContent.value || props.maxlength <= 0) return;
    
    const textContent = editorContent.value.innerText || '';
    // 如果已经达到最大长度，阻止输入（除了删除键等）
    if (textContent.length >= props.maxlength) {
      const allowedKeys = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Tab'];
      const ctrlKeys = ['a', 'c', 'v', 'x', 'z', 'y'];
      
      if (!allowedKeys.includes(e.key) && !(e.ctrlKey && ctrlKeys.includes(e.key.toLowerCase()))) {
        e.preventDefault();
        return false;
      }
    }
  };
  
  const handleInput = () => {
    if (editorContent.value) {
      // 计算纯文本长度
      const textContent = editorContent.value.innerText || '';
      currentLength.value = textContent.length;
      
      // 如果设置了最大长度且超过限制，截断内容
      if (props.maxlength > 0 && textContent.length > props.maxlength) {
        // 截断内容（保留HTML标签结构）
        const content = truncateHtml(editorContent.value.innerHTML, props.maxlength);
        editorContent.value.innerHTML = content;
        
        // 更新长度
        currentLength.value = props.maxlength;
        
        // 恢复光标位置到末尾
        const selection = window.getSelection();
        if (editorContent.value.firstChild) {
          const range = document.createRange();
          range.selectNodeContents(editorContent.value);
          range.collapse(false);
          selection.removeAllRanges();
          selection.addRange(range);
        }
      }
      
      emit('update:modelValue', editorContent.value.innerHTML);
    }
  };
  
  // 截断HTML内容但保持标签完整
  const truncateHtml = (html, maxLength) => {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    
    let textLength = 0;
    const result = [];
    
    const traverse = (node) => {
      if (textLength >= maxLength) return;
      
      if (node.nodeType === Node.TEXT_NODE) {
        const remaining = maxLength - textLength;
        if (node.textContent.length > remaining) {
          result.push(node.textContent.substring(0, remaining));
          textLength = maxLength;
        } else {
          result.push(node.textContent);
          textLength += node.textContent.length;
        }
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        const tagName = node.tagName.toLowerCase();
        // 自闭合标签
        const selfClosing = ['br', 'hr', 'img', 'input'];
        
        if (selfClosing.includes(tagName)) {
          result.push(`<${tagName}${getAttributes(node)}>`);
        } else {
          result.push(`<${tagName}${getAttributes(node)}>`);
          node.childNodes.forEach(child => traverse(child));
          if (textLength < maxLength) {
            result.push(`</${tagName}>`);
          }
        }
      }
    };
    
    tempDiv.childNodes.forEach(child => traverse(child));
    
    return result.join('');
  };
  
  // 获取元素属性
  const getAttributes = (node) => {
    const attrs = [];
    for (let i = 0; i < node.attributes.length; i++) {
      attrs.push(` ${node.attributes[i].name}="${node.attributes[i].value}"`);
    }
    return attrs.join('');
  };
  
  const updateFormat = () => {
    currentFormat.value = {
      bold: document.queryCommandState('bold'),
      italic: document.queryCommandState('italic'),
      underline: document.queryCommandState('underline'),
      strikeThrough: document.queryCommandState('strikeThrough'),
      justifyLeft: document.queryCommandState('justifyLeft'),
      justifyCenter: document.queryCommandState('justifyCenter'),
      justifyRight: document.queryCommandState('justifyRight')
    };
  };
  
  onMounted(() => {
    // 初始化内容
    if (editorContent.value && props.modelValue) {
      editorContent.value.innerHTML = props.modelValue;
      const textContent = editorContent.value.innerText || '';
      currentLength.value = textContent.length;
    }
  });
  
  watch(() => props.modelValue, (newVal) => {
    if (editorContent.value && editorContent.value.innerHTML !== newVal) {
      editorContent.value.innerHTML = newVal;
      // 更新字数统计
      const textContent = editorContent.value.innerText || '';
      currentLength.value = textContent.length;
    }
  });
  </script>
  
  <style scoped>
  .rich-editor-container {
    border: 1px solid #dcdfe6;
    border-radius: 4px;
    overflow: hidden;
    background: #fff;
  }
  
  .editor-toolbar {
    display: flex;
    align-items: center;
    padding: 8px;
    background: #f5f7fa;
    border-bottom: 1px solid #e4e7ed;
    flex-wrap: wrap;
    gap: 2px;
  }
  
  .toolbar-btn {
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    border: none;
    background: transparent;
    cursor: pointer;
    border-radius: 4px;
    font-size: 14px;
    color: #606266;
    transition: all 0.2s;
  }
  
  .toolbar-btn:hover {
    background: #e4e7ed;
  }
  
  .toolbar-btn.active {
    background: #409eff;
    color: #fff;
  }
  
  .toolbar-divider {
    width: 1px;
    height: 20px;
    background: #dcdfe6;
    margin: 0 4px;
  }
  
  .toolbar-select {
    height: 28px;
    padding: 0 8px;
    border: 1px solid #dcdfe6;
    border-radius: 4px;
    font-size: 12px;
    color: #606266;
    background: #fff;
    cursor: pointer;
    outline: none;
  }
  
  .toolbar-select:focus {
    border-color: #409eff;
  }
  
  .editor-content {
    min-height: 200px;
    padding: 12px;
    outline: none;
    font-size: 14px;
    line-height: 1.6;
    color: #606266;
    white-space: pre-wrap;
    word-break: break-all;
  }
  
  .editor-content:focus {
    outline: none;
  }
  
  .editor-content p {
    margin: 0 0 8px 0;
  }
  
  .editor-content ul, .editor-content ol {
    margin: 8px 0;
    padding-left: 24px;
  }
  
  .editor-content li {
    margin: 4px 0;
  }
  
  .editor-footer {
    padding: 8px 12px;
    background: #f5f7fa;
    border-top: 1px solid #e4e7ed;
    text-align: right;
    font-size: 12px;
    color: #909399;
  }
  
  .editor-footer .text-warning {
    color: #f56c6c;
  }
  </style>